import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { SaaSGamaLogo } from "@/components/ui/saasgama-logo";
import { Loader2, Shield, CheckCircle2, ArrowRight } from "lucide-react";
import { Link, useLocation } from "wouter";
import { Helmet } from "react-helmet-async";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const resetPasswordSchema = z.object({
  token: z.string().min(1, { message: "Reset token is required" }),
  password: z.string()
    .min(8, { message: "Password must be at least 8 characters" })
    .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, { 
      message: "Password must contain at least one uppercase letter, one lowercase letter, and one number" 
    }),
  confirmPassword: z.string().min(8, { message: "Please confirm your password" }),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type ResetPasswordFormValues = z.infer<typeof resetPasswordSchema>;

export default function ResetPasswordPage() {
  const [location] = useLocation();
  const { toast } = useToast();
  const [isSuccess, setIsSuccess] = useState(false);

  // Extract token from URL parameters - Fix for client-side routing
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token') || '';
  

  const form = useForm<ResetPasswordFormValues>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      token: token,
      password: "",
      confirmPassword: "",
    },
  });

  // Update token if URL changes
  useEffect(() => {
    if (token) {
      form.setValue('token', token);
    }
  }, [token, form]);

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: async (data: ResetPasswordFormValues) => {
      return await apiRequest("/api/reset-password", {
        method: "POST",
        body: data,
      });
    },
    onSuccess: (data: any) => {
      setIsSuccess(true);
      toast({
        title: "Password reset successful",
        description: "Your password has been updated successfully. Redirecting to sign in...",
      });
      
      // Auto-redirect to login after 3 seconds if server indicates redirect
      if (data?.redirectToLogin) {
        setTimeout(() => {
          window.location.href = '/login';
        }, 3000);
      }
    },
    onError: (error: any) => {
      const errorMessage = error.message || "Failed to reset password. Please try again or request a new reset link.";
      
      toast({
        title: "Password Reset Error",
        description: errorMessage,
        variant: "destructive",
      });
      
      // Clear form on certain errors
      if (errorMessage.includes('expired') || errorMessage.includes('used') || errorMessage.includes('invalid')) {
        form.reset({
          token: token,
          password: "",
          confirmPassword: "",
        });
      }
    },
  });

  const handleResetPasswordSubmit = (values: ResetPasswordFormValues) => {
    // Prevent double submission
    if (resetPasswordMutation.isPending) {
      return;
    }
    
    resetPasswordMutation.mutate(values);
  };

  // Show error if no token is provided
  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex">
        <Helmet>
          <title>Reset Password - SaaSGama</title>
          <meta name="description" content="Reset your SaaSGama account password" />
        </Helmet>

        <div className="flex-1 flex items-center justify-center p-4">
          <div className="w-full max-w-md">
            <div className="flex items-center justify-center mb-6">
              <SaaSGamaLogo size="md" />
            </div>

            <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-xl p-6 text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid Reset Link</h2>
              <p className="text-gray-600 mb-6">
                This password reset link is invalid or has expired. Please request a new password reset link.
              </p>
              <Link href="/login">
                <Button className="w-full">
                  Back to Sign In
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex">
      <Helmet>
        <title>Reset Password - SaaSGama</title>
        <meta name="description" content="Create a new password for your SaaSGama account" />
      </Helmet>

      {/* Left Side - Modern Hero Section */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-gray-900 via-blue-900 to-indigo-900 text-white relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-32 h-32 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full blur-xl"></div>
          <div className="absolute bottom-40 right-20 w-40 h-40 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full blur-xl"></div>
          <div className="absolute top-1/2 left-1/3 w-24 h-24 bg-gradient-to-r from-green-400 to-blue-400 rounded-full blur-xl"></div>
        </div>
        
        <div className="relative z-10 h-full flex flex-col justify-center p-6 lg:p-8 max-w-lg mx-auto">
          {/* Logo */}
          <div className="flex items-center justify-center mb-6">
            <SaaSGamaLogo size="lg" className="text-white" />
          </div>

          <h1 className="text-3xl lg:text-4xl font-bold mb-4 leading-tight">
            Secure password
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent block">
              reset process
            </span>
          </h1>
          
          <p className="text-lg text-gray-300 mb-6 leading-relaxed">
            Create a strong new password to keep your software portfolio secure and protected.
          </p>

          {/* Security features */}
          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Enhanced Security</h3>
                <p className="text-gray-400">Your password is encrypted with industry-standard security</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-xl flex items-center justify-center flex-shrink-0">
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-lg mb-1">Verified Reset Link</h3>
                <p className="text-gray-400">This secure link expires after 24 hours for your protection</p>
              </div>
            </div>
          </div>

          {/* Trust indicators */}
          <div className="mt-12 pt-8 border-t border-white/10">
            <p className="text-sm text-gray-400 mb-4">Trusted security practices</p>
            <div className="flex items-center space-x-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-white">256-bit</div>
                <div className="text-xs text-gray-400">Encryption</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">24hrs</div>
                <div className="text-xs text-gray-400">Link Expiry</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-white">99.9%</div>
                <div className="text-xs text-gray-400">Uptime</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Reset Form */}
      <div className="flex-1 flex items-center justify-center p-4 lg:p-8 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center mb-6">
            <SaaSGamaLogo size="md" />
          </div>

          <div className="bg-white/80 backdrop-blur-sm rounded-2xl border border-gray-200 shadow-xl p-6">
            {!isSuccess ? (
              <>
                <div className="text-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-1">Create New Password</h2>
                  <p className="text-sm text-gray-600">Enter your new password below</p>
                </div>

                <Form {...form}>
                  <form onSubmit={form.handleSubmit(handleResetPasswordSubmit)} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-700 font-medium">New Password</FormLabel>
                          <FormControl>
                            <Input 
                              type="password"
                              placeholder="Enter your new password" 
                              className="h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-xl"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-gray-700 font-medium">Confirm New Password</FormLabel>
                          <FormControl>
                            <Input 
                              type="password"
                              placeholder="Confirm your new password" 
                              className="h-12 border-gray-300 focus:border-blue-500 focus:ring-blue-500 rounded-xl"
                              {...field} 
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button 
                      type="submit" 
                      className="w-full h-12 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 transform hover:-translate-y-0.5" 
                      disabled={resetPasswordMutation.isPending || isSuccess}
                    >
                      {resetPasswordMutation.isPending ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Updating password...
                        </>
                      ) : (
                        <>
                          Update Password
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </>
                      )}
                    </Button>
                  </form>
                </Form>
              </>
            ) : (
              <>
                <div className="text-center">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Password Updated Successfully!</h2>
                  <p className="text-gray-600 mb-4">
                    Your SaaSGama account password has been securely updated. For your protection, you've been signed out from all devices.
                  </p>
                  <p className="text-sm text-gray-500 mb-6">
                    You'll be automatically redirected to the sign in page in a few seconds...
                  </p>
                  <Link href="/login">
                    <Button className="w-full h-12 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-xl">
                      Continue to Sign In
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </div>

          {/* Back to login link */}
          <div className="text-center mt-6">
            <Link href="/login" className="text-gray-500 hover:text-blue-600 text-sm font-medium transition-colors">
              ← Back to sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}